import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Wrench, Search, AlertTriangle, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

/**
 * RepairOrServiceDialog — dialog unificat pentru:
 *  - REPARAȚIE (mode='repair'): user selectează ce piese a folosit + cantitate. Scade din stoc.
 *  - REVIZIE (mode='service'): la fel, dar actualizează și data/km reviziei.
 *
 * Backend: POST /cars/{cid}/repair cu body.parts=[{item_id, qty}], oil_liters, note, is_service, km.
 *
 * Props:
 *   - car: full car object
 *   - mode: 'repair' | 'service'
 *   - onClose, onDone
 */
export default function RepairOrServiceDialog({ car, mode = "repair", defects = [], onClose, onDone }) {
  const isService = mode === "service";
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false); // include și piese fără linked_car_id (universale)
  // selecție: { item_id: qty }
  const [selection, setSelection] = useState({});
  const [oilLiters, setOilLiters] = useState(isService ? Number(car.oil_liters_per_service || 0) : 0);
  const [oilStock, setOilStock] = useState(0);
  const [note, setNote] = useState("");
  const [kmInput, setKmInput] = useState(String(Math.round(car.km || car.kilometri || 0)));
  const [submitting, setSubmitting] = useState(false);
  // Defect raportat anterior selectat pentru rezolvare (doar pentru repair mode)
  const [selectedDefectId, setSelectedDefectId] = useState(() => {
    // Auto-select unicul defect deschis dacă există unul singur
    const openDefects = (defects || []).filter((d) => !d.fixed);
    return openDefects.length === 1 ? openDefects[0].id : "";
  });

  const openDefects = useMemo(() => (defects || []).filter((d) => !d.fixed), [defects]);
  const selectedDefect = useMemo(() => openDefects.find((d) => d.id === selectedDefectId), [openDefects, selectedDefectId]);

  // Când se schimbă defect-ul selectat, pre-completăm nota dacă e goală
  useEffect(() => {
    if (selectedDefect && !note.trim()) {
      setNote(`Rezolvat: ${selectedDefect.reason || ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDefectId]);

  // Load piese + suggested filtre (pentru revizie)
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/cars/${car.id}/spare-parts`),
      showAll ? api.get("/inventory").then((r) => (r.data || []).filter((it) => it.item_type === "piesa_auto" && !it.linked_car_id)) : Promise.resolve([]),
      api.get("/inventory/oil-stock").catch(() => ({ data: { total_liters: 0 } })),
    ]).then(([carParts, universal, oil]) => {
      const all = [...(carParts.data || []), ...universal];
      // dedup pe id
      const seen = new Set();
      const merged = all.filter((p) => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      // Stoc disponibil > 0
      const available = merged.filter((p) => (Number(p.quantity) || 0) > 0 && p.status !== "defect");
      setParts(available);
      setOilStock(Number(oil.data?.total_liters || 0));

      // La revizie: pre-suggestions — piese care match cu car_consumable_type
      if (isService) {
        const suggestedTypes = [];
        if (car.oil_liters_per_service) suggestedTypes.push("ulei_motor");
        if (car.filter_oil_type) suggestedTypes.push("filtru_ulei");
        if (car.filter_air_type) suggestedTypes.push("filtru_aer");
        if (car.filter_fuel_type) suggestedTypes.push("filtru_combustibil");
        if (car.filter_cabin_type) suggestedTypes.push("filtru_habitaclu");
        if (suggestedTypes.length > 0) {
          const presel = {};
          for (const t of suggestedTypes) {
            const cand = available.find((p) => p.car_consumable_type === t);
            if (cand) presel[cand.id] = 1;
          }
          setSelection((s) => ({ ...presel, ...s }));
        }
      }
    }).catch(() => setParts([]))
      .finally(() => setLoading(false));
  }, [car.id, showAll, isService, car.oil_liters_per_service, car.filter_oil_type, car.filter_air_type, car.filter_fuel_type, car.filter_cabin_type, car.km, car.kilometri]);

  const toggle = (p) => {
    setSelection((s) => {
      const n = { ...s };
      if (n[p.id] != null) delete n[p.id];
      else n[p.id] = 1;
      return n;
    });
  };

  const setQty = (id, qty) => {
    setSelection((s) => ({ ...s, [id]: Math.max(0, Number(qty) || 0) }));
  };

  const incQty = (id, delta) => {
    setSelection((s) => ({ ...s, [id]: Math.max(0, (Number(s[id]) || 0) + delta) }));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parts;
    return parts.filter((p) => `${p.name || ""} ${p.serial || ""} ${p.supplier || ""} ${p.equipment_category || ""}`.toLowerCase().includes(q));
  }, [parts, search]);

  // Sort: selecție primul, apoi name
  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aSel = selection[a.id] != null ? 0 : 1;
      const bSel = selection[b.id] != null ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return (a.name || "").localeCompare(b.name || "", "ro");
    });
  }, [filtered, selection]);

  const totals = useMemo(() => {
    const n = Object.values(selection).filter((q) => q > 0).length;
    const totalQty = Object.values(selection).reduce((s, q) => s + (Number(q) || 0), 0);
    const totalValue = Object.entries(selection).reduce((s, [id, q]) => {
      const p = parts.find((x) => x.id === id);
      const price = Number(p?.price_chf) || 0;
      return s + (price * (Number(q) || 0));
    }, 0);
    return { n, totalQty, totalValue };
  }, [selection, parts]);

  const oilOk = Number(oilLiters || 0) <= oilStock;

  const submit = async () => {
    setSubmitting(true);
    try {
      const partsPayload = Object.entries(selection)
        .filter(([, q]) => Number(q) > 0)
        .map(([item_id, qty]) => ({ item_id, qty: Number(qty) }));
      const { data } = await api.post(`/cars/${car.id}/repair`, {
        parts: partsPayload,
        oil_liters: Number(oilLiters || 0),
        note: note.trim() || null,
        is_service: isService,
        km: isService ? Number(kmInput || 0) : null,
        defect_id: !isService && selectedDefectId ? selectedDefectId : null,
      });
      const used = (data?.used_parts || []).length;
      const oilTxt = data?.oil_consumed > 0 ? ` + ${data.oil_consumed} L ulei` : "";
      const intervention = isService ? "Revizie" : "Reparație";
      if (data?.oil_shortage > 0) {
        toast.warning(`${intervention}: ${used} piese${oilTxt} · lipsă ${data.oil_shortage} L ulei!`);
      } else {
        toast.success(`${intervention} înregistrată — ${used} piese${oilTxt}`);
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setSubmitting(false);
    }
  };

  const titleColor = isService ? "text-emerald-700" : "text-blue-700";
  const titleIcon = isService ? "🔧" : "🛠";
  const titleText = isService ? "REVIZIE" : "REPARAȚIE";
  const submitBg = isService ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700";

  return (
    <Dialog open onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid={`${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${titleColor}`}>
            <span className="text-xl">{titleIcon}</span>
            {titleText}: {car.marca} {car.model} ({car.numar_inmatriculare})
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isService
              ? "Selectează piesele consumate la revizie. Stocul se actualizează automat. Data și km reviziei se salvează."
              : "Selectează piesele folosite la reparație. Stocul se actualizează automat. Mașina revine la status funcțional."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
          {/* Selector defect raportat (DOAR la reparație) */}
          {!isService && openDefects.length > 0 && (
            <div className="rounded-md border-2 border-red-300 bg-red-50/60 p-3" data-testid="defect-selector">
              <Label className="small-label flex items-center gap-1.5 text-red-900">
                <AlertTriangle size={14} className="text-red-600"/>
                Defect raportat anterior (selectează pentru a marca rezolvarea)
              </Label>
              <div className="mt-2 space-y-1.5">
                {openDefects.map((d) => {
                  const isSel = selectedDefectId === d.id;
                  const sevColors = {
                    high:   "border-red-500 bg-red-100 text-red-900",
                    medium: "border-amber-500 bg-amber-100 text-amber-900",
                    low:    "border-blue-400 bg-blue-100 text-blue-900",
                  };
                  const sevLabel = { high: "GRAV", medium: "MEDIU", low: "MIC" };
                  return (
                    <label
                      key={d.id}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${isSel ? "border-red-600 bg-white ring-2 ring-red-400" : "border-zinc-300 bg-white hover:border-red-300"}`}
                      data-testid={`defect-option-${d.id}`}
                    >
                      <input
                        type="radio"
                        name="defect-select"
                        checked={isSel}
                        onChange={() => setSelectedDefectId(d.id)}
                        className="mt-1 accent-red-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${sevColors[d.severity] || sevColors.medium}`}>
                            {sevLabel[d.severity] || "MEDIU"}
                          </span>
                          <span className="font-semibold text-sm">{d.reason || "—"}</span>
                          {d.spare_part_needed && (
                            <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-900 border border-orange-300">
                              🔧 {d.spare_part_needed}
                            </span>
                          )}
                        </div>
                        {d.reported_at && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">📅 raportat: {new Date(d.reported_at).toLocaleDateString("ro-RO")}{d.reported_by ? ` · de ${d.reported_by}` : ""}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
                {selectedDefectId && (
                  <button
                    type="button"
                    onClick={() => setSelectedDefectId("")}
                    className="text-[11px] text-zinc-500 hover:text-zinc-800 underline mt-1"
                    data-testid="defect-clear"
                  >
                    × Deselectează (reparație fără defect raportat)
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Toggle universal parts + search */}
          <div className="flex items-center gap-2 flex-wrap sticky top-0 bg-white py-2 z-10 border-b">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută piesă..." className="pl-7 h-9" data-testid={`${mode}-search`}/>
            </div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={showAll} onCheckedChange={(v) => setShowAll(!!v)} data-testid={`${mode}-show-universal`}/>
              <span>+ Piese universale (fără mașină atribuită)</span>
            </label>
            <div className="ml-auto text-[11px] flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">{totals.n} {totals.n === 1 ? "piesă" : "piese"}</span>
              {totals.totalValue > 0 && <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 font-bold">{totals.totalValue.toFixed(2)} CHF</span>}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-zinc-500">Se încarcă piesele...</div>
          ) : sortedFiltered.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 italic text-sm">
              {showAll
                ? "Nicio piesă disponibilă pe stoc."
                : `Nicio piesă disponibilă pe stoc pentru această mașină. Bifează „Piese universale" pentru a vedea și piesele neatribuite.`}
            </div>
          ) : (
            <div className="border rounded-md divide-y">
              {sortedFiltered.map((p) => {
                const isSel = selection[p.id] != null;
                const qty = Number(selection[p.id]) || 0;
                const avail = Number(p.quantity) || 0;
                const overstock = qty > avail;
                return (
                  <div
                    key={p.id}
                    className={`p-2.5 flex items-center gap-3 transition-colors ${isSel ? "bg-blue-50" : "hover:bg-zinc-50"}`}
                    data-testid={`${mode}-part-row-${p.id}`}
                  >
                    <Checkbox checked={isSel} onCheckedChange={() => toggle(p)} data-testid={`${mode}-part-${p.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        <span className="truncate">{p.name}</span>
                        {!p.linked_car_id && <span className="text-[9px] font-bold uppercase bg-zinc-200 text-zinc-700 px-1 py-0.5 rounded">UNIVERSAL</span>}
                        {p.car_consumable_type && <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-800 px-1 py-0.5 rounded">{p.car_consumable_type}</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 flex gap-2 flex-wrap mt-0.5">
                        <span>Stoc: <strong className={avail <= 1 ? "text-amber-700" : "text-zinc-700"}>{avail}{p.unit ? ` ${p.unit}` : " buc"}</strong></span>
                        {p.location && <span>📍 {p.location}</span>}
                        {p.supplier && <span>🏭 {p.supplier}</span>}
                        {p.price_chf != null && <span className="text-emerald-700 font-bold">{Number(p.price_chf).toFixed(2)} CHF/buc</span>}
                      </div>
                    </div>
                    {isSel && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => incQty(p.id, -1)} disabled={qty <= 0} data-testid={`${mode}-qty-minus-${p.id}`}>
                          <Minus size={12}/>
                        </Button>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={qty}
                          onChange={(e) => setQty(p.id, e.target.value)}
                          className={`h-7 w-16 text-center text-sm ${overstock ? "border-red-400 text-red-700" : ""}`}
                          data-testid={`${mode}-qty-${p.id}`}
                        />
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => incQty(p.id, 1)} disabled={qty >= avail} data-testid={`${mode}-qty-plus-${p.id}`}>
                          <Plus size={12}/>
                        </Button>
                        {overstock && (
                          <span className="text-[10px] text-red-700 font-bold flex items-center gap-0.5">
                            <AlertTriangle size={10}/>peste stoc
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Ulei + km + notă */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t">
            <div>
              <Label className="small-label">Ulei consumat (L)</Label>
              <Input type="number" step="0.1" min="0" value={oilLiters} onChange={(e) => setOilLiters(e.target.value)} className="mt-1" data-testid={`${mode}-oil`}/>
              <div className={`text-[10px] mt-0.5 ${oilOk ? "text-zinc-500" : "text-red-700 font-bold"}`}>
                Stoc: {oilStock.toFixed(2)} L {!oilOk && "⚠ insuficient"}
              </div>
            </div>
            {isService && (
              <div>
                <Label className="small-label">Km la revizie</Label>
                <Input type="number" min="0" value={kmInput} onChange={(e) => setKmInput(e.target.value)} className="mt-1" data-testid={`${mode}-km`}/>
                <div className="text-[10px] mt-0.5 text-zinc-500">Updateză km mașină dacă e mai mare</div>
              </div>
            )}
            <div className={isService ? "" : "md:col-span-2"}>
              <Label className="small-label">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={isService ? "ex: revizie standard la 80.000 km" : "ex: schimbat pompă apă"} className="mt-1 text-sm" data-testid={`${mode}-note`}/>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-3 mt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button
            onClick={submit}
            disabled={submitting || (totals.n === 0 && Number(oilLiters || 0) === 0)}
            className={`${submitBg} text-white`}
            data-testid={`${mode}-submit`}
          >
            <Wrench size={14} className="mr-1.5"/>
            {submitting ? "Se înregistrează..." : `Înregistrează ${isService ? "revizia" : "reparația"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
